import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import heroImg from "@/assets/hero-contact.jpg";

const ContactPage = () => {
  usePageSeo("contact");
  const { getSetting } = useSiteSettings();
  const whatsapp = getSetting("whatsapp_number", "");
  const phone = getSetting("phone", "");
  const email = getSetting("email", "");
  const address = getSetting("address", "");

  const [name, setName] = useState("");
  const [email_, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email_ || !message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email_}\n\n${message}`);
    const subj = encodeURIComponent(subject || "Website inquiry");
    window.location.href = `mailto:${email || "info@allentours.com"}?subject=${subj}&body=${body}`;
    toast.success("Opening your email app…");
  };

  const items = ([
    phone && { icon: Phone, label: "Call us", value: phone, href: `tel:${phone.replace(/[^+\d]/g, "")}` },
    email && { icon: Mail, label: "Email us", value: email, href: `mailto:${email}` },
    address && { icon: MapPin, label: "Visit", value: address, href: "#" },
    { icon: Clock, label: "Hours", value: "24/7 concierge", href: "#" },
  ].filter(Boolean)) as { icon: typeof Phone; label: string; value: string; href: string }[];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageHero
        eyebrow="Contact"
        title={<>We're here, <span className="italic text-gradient-ocean">whenever you are.</span></>}
        subtitle="Questions, custom itineraries, last-minute changes — talk to a real human, not a bot. We typically reply within an hour."
        image={heroImg}
      />

      <section className="container mx-auto px-4 py-20 grid lg:grid-cols-5 gap-10">
        {/* Info */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <a
              key={it.label}
              href={it.href}
              className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-border/60 shadow-soft hover:shadow-elegant transition-smooth"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-ocean text-primary-foreground flex items-center justify-center shadow-glow shrink-0">
                <it.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{it.label}</div>
                <div className="font-display font-semibold text-lg mt-0.5">{it.value}</div>
              </div>
            </a>
          ))}
          {whatsapp && (
            <a
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-5 rounded-2xl bg-[hsl(142,70%,40%)] text-white shadow-soft hover:shadow-elegant transition-smooth"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">Chat on WhatsApp</span>
            </a>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-3 bg-gradient-card rounded-3xl p-8 md:p-10 shadow-card border border-border/60 space-y-5">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">Send a message</span>
            <h2 className="font-display text-3xl font-semibold mt-2">Tell us about your trip.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl" placeholder="Your full name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={email_} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" placeholder="you@email.com" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject</Label>
            <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-12 rounded-xl" placeholder="How can we help?" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message">Message *</Label>
            <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="rounded-xl" placeholder="Tell us about your travel dates, group size, and what you'd love to do…" />
          </div>
          <Button type="submit" size="lg" className="rounded-full bg-gradient-ocean text-primary-foreground shadow-glow hover:-translate-y-0.5 transition-smooth font-semibold">
            <Send className="w-4 h-4 mr-2" /> Send message
          </Button>
        </form>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
